import { traduzirErroSupabase } from '@octawash/shared';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeOff, Loader2, Lock, Mail, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

/**
 * Logo Google em SVG inline (multicor oficial) — pra botão "Continuar com Google"
 * ficar com cara de consumer app de verdade, não placeholder.
 */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#EA4335"
        d="M12 5.04c1.62 0 3.07.56 4.21 1.65l3.16-3.16C17.45 1.66 14.97.6 12 .6 7.41.6 3.43 3.27 1.5 7.13l3.69 2.86C6.07 7.06 8.79 5.04 12 5.04z"
      />
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.55-.2-2.27H12v4.29h6.46c-.28 1.51-1.13 2.79-2.41 3.66l3.66 2.84c2.14-1.97 3.38-4.88 3.38-8.52z"
      />
      <path
        fill="#FBBC05"
        d="M5.19 14.43A7.07 7.07 0 014.8 12c0-.84.14-1.66.39-2.43L1.5 6.71A11.97 11.97 0 00.6 12c0 1.91.45 3.72 1.25 5.34l3.34-2.91z"
      />
      <path
        fill="#34A853"
        d="M12 23.4c3.24 0 5.95-1.07 7.94-2.91l-3.66-2.84c-1.02.69-2.32 1.09-4.28 1.09-3.21 0-5.93-2.02-6.91-4.83l-3.39 2.62C3.43 20.73 7.41 23.4 12 23.4z"
      />
    </svg>
  );
}

export function LoginPage() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);

  async function entrar(e: React.FormEvent) {
    e.preventDefault();
    setCarregando(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password: senha,
    });
    setCarregando(false);
    if (error) {
      toast.error('Falha no login', { description: traduzirErroSupabase(error) });
      return;
    }
    navigate(await rotaPosLogin(data?.user?.id), { replace: true });
  }

  /**
   * Decide pra onde mandar o cliente apos login. Se ainda nao concluiu
   * o onboarding (flag ind_onboarding_completo = false) -> /onboarding.
   * Caso contrario -> home. Best-effort: qualquer falha cai pra home
   * (jamais bloqueia o login).
   *
   * Nota: como ind_onboarding_completo ainda nao esta em database.types.ts,
   * usamos cast pra `any` no select. Atualizar via `npm run db:types` apos
   * a migration 20260522100000_add_ind_onboarding_completo.sql rodar.
   */
  async function rotaPosLogin(authUserId: string | undefined): Promise<string> {
    if (!authUserId) return '/';
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;
      const { data: cli } = await sb
        .from('tab_clientes')
        .select('ind_onboarding_completo')
        .eq('cod_auth_user', authUserId)
        .maybeSingle();
      if (!cli) return '/';
      return cli.ind_onboarding_completo === false ? '/onboarding' : '/';
    } catch {
      return '/';
    }
  }

  async function loginGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/` },
    });
    if (error) toast.error('Falha Google', { description: traduzirErroSupabase(error) });
  }

  const nomeMarca = tenant?.nom_tenant ?? 'Octa Pontos';
  const iniciais = tenant?.nom_tenant ? nomeMarca.slice(0, 2).toUpperCase() : 'OW';

  return (
    <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Blobs morphing flutuantes no fundo (3 camadas) */}
      <div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
      />
      <div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
        style={{ animationDelay: '-4s' }}
      />
      <div
        aria-hidden
        className="octa-blob pointer-events-none absolute right-1/4 top-1/4 h-72 w-72 bg-violet-400"
        style={{ animationDelay: '-8s', opacity: 0.25 }}
      />

      <div className="relative z-10 w-full max-w-sm space-y-6">
        {/* Logo grande + nome marca — entrada com scale + bounce premium */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.85, y: -6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
        >
          {tenant?.des_logo_url ? (
            <img
              src={tenant.des_logo_url}
              alt={nomeMarca}
              className="shadow-octa-glow octa-float mx-auto mb-4 h-20 w-20 rounded-3xl object-cover ring-4 ring-white/40"
            />
          ) : (
            <div className="bg-octa-gradient shadow-octa-glow octa-float mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl ring-4 ring-white/40">
              <span className="text-2xl font-bold text-white">{iniciais}</span>
            </div>
          )}
          <motion.h1
            className="text-display-md font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
          >
            {nomeMarca}
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-1.5 text-sm"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
          >
            Seu carro brilhando em poucos minutos.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <Card variant="glass" className="shadow-octa-lg rounded-3xl border-white/40">
            <CardContent className="space-y-5 p-6">
              <form onSubmit={entrar} className="octa-stagger space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">
                    E-mail
                  </Label>
                  <div className="relative">
                    <Mail
                      aria-hidden
                      className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="h-11 pl-9"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="senha" className="text-xs font-semibold uppercase tracking-wider">
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock
                      aria-hidden
                      className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    />
                    <Input
                      id="senha"
                      type={mostrarSenha ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="h-11 pl-9 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarSenha((v) => !v)}
                      aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      className="text-muted-foreground hover:text-foreground absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={carregando}
                  className="octa-cta-magic group h-12 w-full rounded-xl text-base font-bold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {carregando ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Entrando…
                    </>
                  ) : (
                    <>
                      Entrar
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </Button>
              </form>

              <div className="text-muted-foreground flex items-center gap-3 text-xs font-medium uppercase tracking-wider">
                <hr className="flex-1 border-dashed" />
                ou
                <hr className="flex-1 border-dashed" />
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={loginGoogle}
                className="hover:bg-accent h-12 w-full rounded-xl border-2 text-base font-semibold transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <GoogleIcon className="mr-2 h-5 w-5" />
                Continuar com Google
              </Button>

              <p className="text-muted-foreground pt-1 text-center text-xs">
                <a
                  href="/cadastro"
                  className="text-primary inline-flex items-center gap-1 font-semibold hover:underline"
                >
                  <Sparkles className="h-3 w-3" />
                  Criar conta nova
                </a>
                <span className="mx-2">·</span>
                <a href="/esqueci-senha" className="hover:text-foreground hover:underline">
                  Esqueci a senha
                </a>
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.p
          className="text-muted-foreground text-center text-[11px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          Ao continuar você concorda com nossos termos e a política de privacidade.
        </motion.p>
      </div>
    </div>
  );
}
